import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/auth.service";
import { analyticsService } from "../services/analytics.service";
import { colocService } from "../services/coloc.service";
import { User } from "../types/auth";
import { useSelectedColoc } from "../hooks/useSelectedColoc";
import { useCapabilities } from "../contexts/CapabilitiesContext";
import ExpensesChart from "../components/ExpensesChart";
import PublicationCard from "../components/PublicationCard";
import CoupDOeil from "../components/CoupDOeil";
import ConsommationEnergie from "../components/ConsommationEnergie";
import "../styles/DashboardPage.css";

const DashboardPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const selectedColoc = useSelectedColoc();
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [hasColocs, setHasColocs] = useState<boolean | null>(null);

  const navigate = useNavigate();
  // Le graphe de dépenses est alimenté par les FACTURES, capacité hors offre.
  // Il s'affichait à tout le monde, y compris aux comptes gratuits pour qui la
  // fonctionnalité est masquée partout ailleurs. `granted` seulement : l'état
  // `unlockable` n'existe pas ici puisque aucun plan ne la vend.
  const { stateOf, refresh } = useCapabilities();
  const depensesVisibles = stateOf('invoices') === 'granted';

  // Retour de Stripe : le catalogue des capacités est chargé UNE fois par
  // session, et celui en mémoire date d'avant le paiement. Sans ce
  // rafraîchissement, la carte de publication restait sur son état « à
  // débloquer » et renvoyait vers le choix du plan — juste après l'avoir payé.
  //
  // Deux tentatives espacées : Stripe redirige dès le paiement accepté, mais
  // c'est le webhook `invoice.paid` qui ouvre l'accès, et rien ne garantit
  // qu'il soit arrivé à cet instant. Un seul appel gagnerait la course la
  // plupart du temps, pas toujours.
  const paiementConfirme = new URLSearchParams(window.location.search).get('checkout') === 'success';
  useEffect(() => {
    if (!paiementConfirme) return;
    refresh();
    const t = setTimeout(refresh, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paiementConfirme]);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate("/signin");
      return;
    }
    setUser(currentUser.user);

    colocService.getColocs().then((colocs) => {
      setHasColocs(colocs.length > 0);
    });
  }, [navigate]);

  useEffect(() => {
    if (!selectedColoc || !depensesVisibles) return;

    const fetchExpenseData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await analyticsService.getMonthlyExpenseAnalytics(selectedColoc.id, currentYear);
        setExpenseData(data);
      } catch (err) {

        setError("Impossible de charger les données de dépenses");
      } finally {
        setLoading(false);
      }
    };

    fetchExpenseData();
  }, [selectedColoc, currentYear, depensesVisibles]);

  const handleYearChange = (year: number) => {
    setCurrentYear(year);
  };

  if (!user) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="dashboard-wrapper">
      <h2>Tableau de bord</h2>

      {/* Accusé de réception du paiement. Sans lui, le retour de Stripe ne
          disait RIEN : un propriétaire venant d'enregistrer sa carte atterrissait
          sur « Créez votre première colocation », exactement comme s'il n'avait
          rien fait. Sur le chemin de l'argent, c'est le pire endroit où rester
          muet — il ne sait pas si son paiement est passé. */}
      {paiementConfirme && (
        <div className="paiement-confirme" role="status">
          <strong>Votre essai de 14 jours a commencé.</strong>
          <span>
            Votre carte est enregistrée&nbsp;; aucun prélèvement avant la fin de
            l&apos;essai, et vous pouvez résilier à tout moment depuis
            «&nbsp;Mon compte&nbsp;».
          </span>
        </div>
      )}

      {selectedColoc ? (
        <div className="content-section">
          <CoupDOeil colocId={selectedColoc.id} />
          {/* Surface de conversion (saas/12) : état de publication du site.
              `GoLiveChecklist` était affichée juste en dessous ; retirée le
              2026-08-18 car redondante avec la liste des manques que
              PublicationCard tire désormais de l'API. Le composant est
              conservé, seul son affichage est suspendu. */}
          <PublicationCard coloc={selectedColoc} />
          {/* Les consommations avant le détail des dépenses : elles répondent à
              la question qu'on se pose en arrivant — est-ce que ça monte, et
              est-ce le prix ou l'usage ? Soumises à la même capacité que les
              dépenses, dont elles sont la lecture physique. */}
          {depensesVisibles && <ConsommationEnergie colocId={selectedColoc.id} />}
          {depensesVisibles && (
            <ExpensesChart
              data={expenseData}
              loading={loading}
              error={error}
              onYearChange={handleYearChange}
            />
          )}
        </div>
      ) : hasColocs === false ? (
        <div className="no-coloc-message" style={{ textAlign: "center", padding: "60px 20px" }}>
          <h3>Bienvenue sur Coloc App !</h3>
          <p style={{ marginBottom: "20px", color: "#666" }}>
            Commencez par créer votre première colocation pour accéder à toutes les fonctionnalités.
          </p>
          <button
            className="save-button"
            onClick={() => navigate("/create-coloc")}
            style={{ fontSize: "16px", padding: "12px 24px" }}
          >
            Créer ma première colocation
          </button>
        </div>
      ) : (
        <div className="no-coloc-message">
          <p>Aucune colocation sélectionnée. Veuillez sélectionner une colocation dans le menu à gauche.</p>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
